import { useTenantStore } from "@/stores/tenant-store";

/**
 * API error with structured response data.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly statusText: string,
    public readonly data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * Check if this is a specific HTTP status error.
   */
  isStatus(status: number): boolean {
    return this.status === status;
  }

  /**
   * Check if this is an authentication error (401).
   */
  isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Check if this is a forbidden error (403).
   */
  isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Check if this is a not found error (404).
   */
  isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Check if this is a validation error (400 or 422).
   */
  isValidationError(): boolean {
    return this.status === 400 || this.status === 422;
  }
}

/**
 * Options for API fetch requests.
 */
export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /**
   * Skip adding the x-tenant-id header.
   * Use for public endpoints or auth routes that don't require tenant context.
   */
  skipTenantHeader?: boolean;

  /**
   * Request body (will be JSON stringified).
   */
  body?: unknown;

  /**
   * Custom base URL (defaults to empty for same-origin requests).
   */
  baseUrl?: string;
}

/**
 * Make an API request with automatic tenant header injection.
 *
 * Features:
 * - Automatically adds x-tenant-id header from Zustand store
 * - Always includes credentials for session cookies
 * - Handles JSON serialization/deserialization
 * - Throws ApiError for non-OK responses
 *
 * @example
 * ```ts
 * // GET request
 * const accounts = await apiFetch<Account[]>("/api/accounts");
 *
 * // POST request
 * const newAccount = await apiFetch<Account>("/api/accounts", {
 *   method: "POST",
 *   body: { name: "Cash", type: "asset" },
 * });
 *
 * // Skip tenant header for auth endpoints
 * const session = await apiFetch("/api/auth/session", {
 *   skipTenantHeader: true,
 * });
 * ```
 */
export async function apiFetch<T = unknown>(
  endpoint: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const {
    skipTenantHeader = false,
    body,
    baseUrl = "",
    headers: customHeaders,
    ...fetchOptions
  } = options;

  // Access Zustand state non-reactively (doesn't cause re-renders)
  const { activeTenantId: tenantId, activeOrganizationId: organizationId } =
    useTenantStore.getState();

  const isMultipart = body instanceof FormData;

  // Build headers
  const headers: HeadersInit = {
    ...(!isMultipart ? { "Content-Type": "application/json" } : {}),
    Accept: "application/json",
    ...(customHeaders as Record<string, string>),
  };

  // Add tenant header if available and not explicitly skipped
  if (tenantId && !skipTenantHeader) {
    (headers as Record<string, string>)["x-tenant-id"] = tenantId;
    if (organizationId) {
      (headers as Record<string, string>)["x-organization-id"] = organizationId;
    }
  }

  // Build full URL
  const url = `${baseUrl}${endpoint}`;

  // Make request
  const response = await fetch(url, {
    ...fetchOptions,
    credentials: "include", // Always include session cookies
    headers,
    body: isMultipart
      ? body
      : body !== undefined
        ? JSON.stringify(body)
        : undefined,
  });

  if (!response.ok) throw await apiResponseError(response);

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as T;
  }

  // Handle responses with no content-length or empty body
  const contentLength = response.headers.get("content-length");
  if (contentLength === "0") {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// Convenience methods
apiFetch.get = <T = unknown>(
  url: string,
  options?: Omit<ApiFetchOptions, "method" | "body">,
): Promise<T> => apiFetch<T>(url, { ...options, method: "GET" });

apiFetch.post = <T = unknown>(
  url: string,
  body?: unknown,
  options?: Omit<ApiFetchOptions, "method" | "body">,
): Promise<T> =>
  apiFetch<T>(url, { ...options, method: "POST", body: body ?? {} });

apiFetch.put = <T = unknown>(
  url: string,
  body?: unknown,
  options?: Omit<ApiFetchOptions, "method" | "body">,
): Promise<T> =>
  apiFetch<T>(url, { ...options, method: "PUT", body: body ?? {} });

apiFetch.patch = <T = unknown>(
  url: string,
  body?: unknown,
  options?: Omit<ApiFetchOptions, "method" | "body">,
): Promise<T> =>
  apiFetch<T>(url, { ...options, method: "PATCH", body: body ?? {} });

apiFetch.delete = <T = unknown>(
  url: string,
  options?: Omit<ApiFetchOptions, "method" | "body">,
): Promise<T> => apiFetch<T>(url, { ...options, method: "DELETE" });

/**
 * Fetch a blob (for file downloads).
 * Includes tenant header and credentials like other apiFetch methods.
 */
apiFetch.blob = async (
  endpoint: string,
  options: Omit<ApiFetchOptions, "body"> = {},
): Promise<Blob> => {
  const {
    skipTenantHeader = false,
    baseUrl = "",
    headers: customHeaders,
    ...fetchOptions
  } = options;

  const { activeTenantId: tenantId, activeOrganizationId: organizationId } =
    useTenantStore.getState();

  const headers: HeadersInit = {
    Accept: "*/*",
    ...(customHeaders as Record<string, string>),
  };

  if (tenantId && !skipTenantHeader) {
    (headers as Record<string, string>)["x-tenant-id"] = tenantId;
    if (organizationId) {
      (headers as Record<string, string>)["x-organization-id"] = organizationId;
    }
  }

  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    ...fetchOptions,
    credentials: "include",
    headers,
  });

  if (!response.ok) throw await apiResponseError(response);

  return response.blob();
};

/** Preserve the same safe structured errors for JSON, multipart and file calls. */
async function apiResponseError(response: Response): Promise<ApiError> {
  let errorData: unknown;
  try {
    errorData = await response.json();
  } catch {
    errorData = { message: response.statusText };
  }
  const message =
    typeof errorData === "object" &&
    errorData !== null &&
    "message" in errorData &&
    typeof errorData.message === "string"
      ? errorData.message
      : response.statusText;
  return new ApiError(message, response.status, response.statusText, errorData);
}
